import React, { useState, useEffect } from 'react';
import {
  Container,
  Stepper,
  Step,
  StepLabel,
  Button,
  Box,
  Paper,
  Typography,
  Stack,
  Link as MuiLink,
} from '@mui/material';

import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotification } from '../../contexts/NotificationContext';
import { bookAppointmentAsGuest, getServices, bookAppointment } from '../../services/api';
import BarberSelection from './BarberSelection';
import ServiceSelection from './ServiceSelection';
import DateTimeSelection from './DateTimeSelection';
import ClientRegistrationForm from './ClientRegistrationForm';
import ConfirmationStep from './ConfirmationStep';
import SuccessStep from './SuccessStep';
import GuestInfoForm from './GuestInfoForm';
import type { Service } from '../../types';
import { Link } from 'react-router-dom';
import { format, parse } from 'date-fns';

// const steps = ['Select Barber', 'Select Service', 'Choose Date & Time'];

interface GuestInfo {
  name: string;
  email: string;
  phone: string;
}

const BookingFlow: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedBarber, setSelectedBarber] = useState<number | null>(null);
  const [selectedService, setSelectedService] = useState<number | null>(null);
  const [selectedServiceDetails, setSelectedServiceDetails] = useState<Service | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [guestInfo, setGuestInfo] = useState<GuestInfo>({
    name: '',
    email: '',
    phone: '',
  });

  // Define steps with translations - remove the duplicate confirmation step for logged-in users
  const stepLabels = user 
    ? [
        t('booking.steps.selectBarber'),
        t('booking.steps.selectService'),
        t('booking.steps.chooseDateAndTime'),
        t('booking.steps.confirm')
      ]
    : [
        t('booking.steps.selectBarber'),
        t('booking.steps.selectService'),
        t('booking.steps.chooseDateAndTime'),
        t('booking.steps.guestInfo'),
        t('booking.steps.confirm')
      ];

  useEffect(() => {
    const fetchServiceDetails = async () => {
      if (selectedService) {
        try {
          const services = await getServices();
          const service = services.find(s => s.id === selectedService);
          if (service) {
            setSelectedServiceDetails(service);
          }
        } catch (error) {
          console.error('Error fetching service details:', error);
          setError('Failed to fetch service details');
        }
      }
    };

    fetchServiceDetails();
  }, [selectedService]);

  const handleBarberSelect = (barberId: number) => {
    setSelectedBarber(barberId);
    setActiveStep(1);
  };

  const handleServiceSelect = (serviceId: number) => {
    setSelectedService(serviceId);
    setActiveStep(2);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleTimeSelect = (date: string, time: string) => {
    setSelectedDate(date);
    setSelectedTime(time);
    // For logged-in users, go directly to confirmation (step 3)
    // For guests, go to guest info (step 3) and then confirmation (step 4)
    setActiveStep(3);
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    showNotification(errorMessage, 'error', 6000);
  };

  const handleGuestInfoSubmit = async (guestInfo?: GuestInfo) => {
    setLoading(true);
    setError('');

    // Validate all required data is present
    if (!selectedBarber) {
      handleError(t('booking.error.selectBarber'));
      setLoading(false);
      return;
    }

    if (!selectedService) {
      handleError(t('booking.error.selectService'));
      setLoading(false);
      return;
    }

    if (!selectedDate || !selectedTime) {
      handleError(t('booking.error.selectDateTime'));
      setLoading(false);
      return;
    }

    try {
      const parsedDate = parse(selectedDate, 'yyyy-MM-dd', new Date());

      if (user) {
        // Authenticated user booking
        const response = await bookAppointment({
          barber_id: selectedBarber,
          service_id: selectedService,
          date: format(parsedDate, 'yyyy-MM-dd'),
          time: selectedTime,
        });

        // Assuming successful if we get a response without error
        setActiveStep(user ? 4 : 5); // Adjust for the success step based on user status
        showNotification(t('booking.success.appointment'), 'success', 5000);
      } else {
        // Guest booking
        if (!guestInfo || !guestInfo.name || !guestInfo.email || !guestInfo.phone) {
          handleError(t('booking.error.fillGuestInfo'));
          setLoading(false);
          return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(guestInfo.email)) {
          handleError(t('booking.error.validEmail'));
          setLoading(false);
          return;
        }

        // Phone validation
        const phoneRegex = /^\+?[\d\s-]{10,}$/;
        if (!phoneRegex.test(guestInfo.phone)) {
          handleError(t('booking.error.validPhone'));
          setLoading(false);
          return;
        }

        try {
          await bookAppointmentAsGuest({
            ...guestInfo,
            barber_id: selectedBarber,
            service_id: selectedService,
            date: format(parsedDate, 'yyyy-MM-dd'),
            time: selectedTime,
          });

          // If we get here without throwing, booking was successful
          setActiveStep(user ? 4 : 5); // Adjust for the success step based on user status
          showNotification(t('booking.success.appointment'), 'success', 5000);
        } catch (error: any) {
          // Handle specific API errors
          if (error.response?.data?.message) {
            handleError(error.response.data.message);
          } else {
            handleError(t('booking.error.createAppointment'));
          }
        }
      }
    } catch (error: any) {
      console.error('Booking error:', error);
      // More detailed error handling
      if (error.response?.data?.message) {
        handleError(error.response.data.message);
      } else if (error.message) {
        handleError(error.message);
      } else {
        handleError(t('booking.error.createAppointment'));
      }
    } finally {
      setLoading(false);
    }
  };

  const resetBookingState = () => {
    setActiveStep(0);
    setSelectedBarber(null);
    setSelectedService(null);
    setSelectedServiceDetails(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setError('');
    setGuestInfo({
      name: '',
      email: '',
      phone: '',
    });
  };

  const renderBookingContent = () => {
    // Adjust the case numbers based on whether the user is logged in
    switch (activeStep) {
      case 0:
        return <BarberSelection onSelect={handleBarberSelect} />;
      case 1:
        return (
          <ServiceSelection
            barberId={selectedBarber!}
            onSelect={handleServiceSelect}
            onBack={handleBack}
          />
        );
      case 2:
        return (
          <DateTimeSelection
            barberId={selectedBarber!}
            serviceId={selectedService!}
            onBack={handleBack}
            onTimeSelect={handleTimeSelect}
          />
        );
      case 3:
        if (!user) {
          // Guest info form for non-logged in users
          return (
            <GuestInfoForm
              guestInfo={guestInfo}
              onGuestInfoChange={setGuestInfo}
              onSubmit={async () => {
                // Move to confirmation step after guest info
                setActiveStep(4);
                return Promise.resolve();
              }}
              onBack={handleBack}
              loading={loading}
            />
          );
        } else {
          // Confirmation step for logged in users
          return selectedServiceDetails ? (
            <ConfirmationStep
              barberId={selectedBarber!}
              service={selectedServiceDetails}
              date={selectedDate!}
              time={selectedTime!}
              onConfirm={handleGuestInfoSubmit}
              onBack={handleBack}
              loading={loading}
            />
          ) : (
            <Typography>{t('app.loading')}</Typography>
          );
        }
      case 4:
        if (!user) {
          // Confirmation step for guests after entering info
          return selectedServiceDetails ? (
            <ConfirmationStep
              barberId={selectedBarber!}
              service={selectedServiceDetails}
              date={selectedDate!}
              time={selectedTime!}
              onConfirm={() => handleGuestInfoSubmit(guestInfo)}
              onBack={() => setActiveStep(3)}
              loading={loading}
            />
          ) : (
            <Typography>{t('app.loading')}</Typography>
          );
        } else {
          // Success step for logged in users
          return (
            <SuccessStep
              barberId={selectedBarber!}
              serviceId={selectedService!}
              date={selectedDate!}
              time={selectedTime!}
              onNewBooking={resetBookingState}
            />
          );
        }
      case 5:
        // Success step for guests
        return (
          <SuccessStep
            barberId={selectedBarber!}
            serviceId={selectedService!}
            date={selectedDate!}
            time={selectedTime!}
            onNewBooking={resetBookingState}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 2, sm: 4 },
          background: (theme) => theme.palette.background.paper,
          backdropFilter: 'blur(20px)',
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Stepper 
            activeStep={activeStep} 
            alternativeLabel
            sx={{ display: activeStep > (user ? 4 : 5) ? 'none' : 'flex' }}
          >
            {stepLabels.map((label, index) => (
              <Step key={index}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box sx={{ mt: 2 }}>
          {renderBookingContent()}
        </Box>
      </Paper>
    </Container>
  );
};

export default BookingFlow; 